import { FieldRows } from "@solomon-apps/shared/src/components/ui/field-rows";
import {
    ToggleGroup,
    ToggleGroupItem
} from "@solomon-apps/shared/src/components/ui/toggle-group";
import React from "react";

interface WalletListEditorProps {
    /** Selected list type, e.g. "Whitelist" | "Blacklist". */
    listType: string;
    onListTypeChange: (next: string) => void;
    /** The allowed list types (from the issuer constraint). */
    options: string[];
    wallets: string[];
    onWalletsChange: (next: string[]) => void;
    /** Locked (issuer-set) → fields greyed out, but still paginable. */
    disabled?: boolean;
}

/**
 * WalletListEditor — the Whitelist / Blacklist control, in two phases:
 * first pick a list type (ToggleGroup); once chosen, the address list
 * unlocks. A thin policy wrapper: it owns the ToggleGroup mode header and
 * the two-phase gating, and delegates the paginated address rows ("+Add
 * Address") to the generic `FieldRows` element. The locked (issuer-set)
 * variant renders the same UI with `disabled` fields — read-only, still
 * paginable.
 */
export function WalletListEditor({
    listType,
    onListTypeChange,
    options,
    wallets,
    onWalletsChange,
    disabled = false
}: WalletListEditorProps): React.ReactElement {
    // Phase 1: pick a list type. Phase 2 (the address list) unlocks once one
    // is selected.
    const selected = options.includes(listType);

    return (
        <div className="flex flex-col gap-2 max-w-xl">
            <ToggleGroup
                variant="outline"
                spacing={2}
                value={[listType]}
                onValueChange={(values) => {
                    if (disabled) return;
                    const next = values[0];
                    if (typeof next === "string") onListTypeChange(next);
                }}
                className={
                    disabled ? "self-start pointer-events-none" : "self-start"
                }
            >
                {options.map((o) => (
                    <ToggleGroupItem key={o} value={o} className="px-5 py-2">
                        {o}
                    </ToggleGroupItem>
                ))}
            </ToggleGroup>

            {selected ? (
                <FieldRows
                    rows={wallets}
                    onChange={onWalletsChange}
                    placeholder="Paste wallet address"
                    sanitize={(raw) => raw.trim()}
                    addLabel="+Add Address"
                    removeLabel="Remove address"
                    disabled={disabled}
                />
            ) : null}
        </div>
    );
}
